import { inject, injectable, injectFromBase } from 'inversify'
import { computed, makeObservable } from 'mobx'
import { FeedbackPresenter } from '@core/feedback/feedback.presenter'
import { Router } from '@core/routing/router'
import type { RouteId } from '@core/routing/router.config'
import { GamesListRepository, type GameRowPm } from './games-list.repository'

export type GameRowVm = {
  readonly key: string
  readonly label: string
  readonly statusLabel: string
  readonly isYourTurn: boolean
  readonly routesTo: RouteId
  readonly params: { readonly gameId: string }
}

@injectable()
@injectFromBase()
export class GamesPagePresenter extends FeedbackPresenter {
  // Base 'app' default is correct: this Presenter never writes feedback of
  // its own (extends FeedbackPresenter only for i18nRepository access) — it
  // has no failure surface to scope.
  @inject(GamesListRepository)
  private gamesListRepository!: GamesListRepository

  @inject(Router)
  private router!: Router

  constructor() {
    super()
    makeObservable(this, {
      viewModel: computed,
    })
  }

  // Defined copy: PM -> VM, property by property (CI-10). Every display
  // condition is resolved to a boolean here and every string is
  // pre-formatted, so the Component renders without transforming anything.
  // Nothing is ever null in the VM — safe defaults instead (CI-9).
  get viewModel() {
    const repository = this.gamesListRepository

    const loadState = repository.loadState
    const isLoading = loadState.status === 'loading'
    const hasError = loadState.status === 'errored'
    const loadErrorMessage = hasError ? this.failureMessage(loadState.error) : ''
    const rows = (repository.gamesPm ?? []).map((game) => this.toRowVm(game))

    return {
      isLoading,
      showLoadError: hasError,
      loadErrorMessage,
      title: this.i18nRepository.t('games.title'),
      loadingLabel: this.i18nRepository.t('games.loading'),
      reloadLabel: this.i18nRepository.t(isLoading ? 'common.loading' : 'common.reload'),
      newGameLabel: this.i18nRepository.t('games.newGame'),
      showEmpty: !isLoading && !hasError && rows.length === 0,
      emptyLabel: this.i18nRepository.t('games.empty'),
      rows,
    }
  }

  private toRowVm(game: GameRowPm): GameRowVm {
    const t = this.i18nRepository.t.bind(this.i18nRepository)
    const opponentName = (game.yourSeat === 'host' ? game.guestName : game.hostName) ?? ''
    const isYourTurn = game.status === 'playing' && game.activeSeat === game.yourSeat
    const statusLabel =
      game.status === 'finished'
        ? t('games.status.finished')
        : game.status !== 'playing'
          ? t('games.status.waiting')
          : isYourTurn
            ? t('games.status.yourTurn')
            : t('games.status.opponentTurn')

    return {
      key: game.gameId,
      label: t('games.row.vs', { opponent: opponentName }),
      statusLabel,
      isYourTurn,
      routesTo: game.status === 'playing' || game.status === 'finished' ? 'Game.Play' : 'Game.Setup',
      params: { gameId: game.gameId },
    }
  }

  load = () => this.gamesListRepository.load()

  goToLobby = () => this.router.goToId('Lobby')

  openGame = (row: GameRowVm) => this.router.goToId(row.routesTo, row.params)
}
